import * as extensionAuth from '@/libs/extensionAuth'

export const setPracticeId = (practiceId) => {
  if (practiceId) {
    localStorage.setItem('practiceId', practiceId.toString())
  } else {
    localStorage.removeItem('practiceId')
  }
}

export const getPracticeId = () => {
  return localStorage.getItem('practiceId')
}

export const login = async (email, password, days) => {
  const res = await post('/auth/login', { email, password, days })
  // In extension mode the backend returns the token in the body (no cookie);
  // hand it to the extension to persist and use for subsequent requests.
  if (extensionAuth.isExtensionMode() && res?.token) {
    await extensionAuth.setToken(res.token)
  }
  return res
}

export const verifyResetSecret = async (email, secret) => {
  return await post('/auth/verify-reset-secret', { email, secret })
}

export const resetPassword = async (email, secret, newPassword) => {
  return await post('/auth/reset-password', { email, secret, newPassword })
}

export const logout = async () => {
  const res = await post('/auth/logout')
  if (extensionAuth.isExtensionMode()) {
    await extensionAuth.clearToken()
  }
  return res
}

export const getCurrentUser = async () => {
  const resJson = await get('/auth/me')
  return resJson.user
}

export const fetchUsers = async (options = {}) => {
  const params = new URLSearchParams()

  // Add all provided parameters to the query string
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })

  const queryString = params.toString()
  const url = `/users${queryString ? '?' + queryString : ''}`

  const resJson = await get(url)
  return resJson;
}

export const fetchUserById = async (userId) => {
  const resJson = await get(`/users/${userId}`)
  return resJson.user;
}

export const createUser = async (userData) => {
  const resJson = await post('/users', userData)
  return resJson.user;
}

export const updateUser = async (userId, userData) => {
  const resJson = await put(`/users/${userId}`, userData)
  return resJson.user;
}

export const deleteUser = async (userId) => {
  const resJson = await del(`/users/${userId}`)
  return resJson;
}

export const fetchPatients = async (options = {}) => {
  const params = new URLSearchParams()

  // Add all provided parameters to the query string
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })

  const queryString = params.toString()
  const url = `/patients${queryString ? '?' + queryString : ''}`

  const resJson = await get(url)
  return resJson;
}

export const fetchPatientById = async (patientId) => {
  const resJson = await get(`/patients/${patientId}`)
  return resJson.patient;
}

export const createPatient = async (patientData) => {
  const resJson = await post('/patients', patientData)
  return resJson.patient;
}

export const updatePatient = async (patientId, patientData) => {
  const resJson = await put(`/patients/${patientId}`, patientData)
  return resJson.patient;
}

export const fetchPatientAnalytics = async (patientId) => {
  const resJson = await get(`/patients/${patientId}/analytics`)
  return resJson.analytics;
}

export const fetchPractices = async () => {
  const resJson = await get('/practices')
  return resJson.practices;
}

export const createPractice = async (practiceData) => {
  const resJson = await post('/practices', practiceData)
  return resJson.practice;
}

export const fetchPracticeById = async (practiceId) => {
  const resJson = await get(`/practices/${practiceId}`)
  return resJson.practice;
}

export const updatePractice = async (practiceId, practiceData) => {
  const resJson = await put(`/practices/${practiceId}`, practiceData)
  return resJson.practice;
}

export const fetchTickets = async (options = {}) => {
  const params = new URLSearchParams()

  // Add all provided parameters to the query string
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })

  const queryString = params.toString()
  const url = `/tickets${queryString ? '?' + queryString : ''}`

  const resJson = await get(url)
  return resJson;
}

export const fetchTicketById = async (ticketId) => {
  const resJson = await get(`/tickets/${ticketId}`)
  return resJson.ticket;
}

export const createTicket = async (ticketData) => {
  const resJson = await post('/tickets', ticketData)
  return resJson.ticket;
}

export const updateTicket = async (ticketId, ticketData) => {
  const resJson = await put(`/tickets/${ticketId}`, ticketData)
  return resJson.ticket;
}

export const deleteTicket = async (ticketId) => {
  const resJson = await del(`/tickets/${ticketId}`)
  return resJson;
}

export const cancelTicket = async (ticketId, data = {}) => {
  const resJson = await post(`/tickets/${ticketId}/cancel`, data)
  return resJson.ticket;
}

export const fetchEvents = async (options = {}) => {
  const params = new URLSearchParams()

  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })

  const queryString = params.toString()
  const url = `/events${queryString ? '?' + queryString : ''}`

  const resJson = await get(url)
  return resJson;
}

export const fetchProducts = async (options = {}) => {
  const params = new URLSearchParams()

  // Add all provided parameters to the query string
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })

  const queryString = params.toString()
  const url = `/products${queryString ? '?' + queryString : ''}`

  const resJson = await get(url)
  return resJson;
}

export const fetchProductById = async (productId) => {
  const resJson = await get(`/products/${productId}`)
  return resJson.product;
}

export const createProduct = async (productData) => {
  const resJson = await post('/products', productData)
  return resJson.product;
}

export const updateProduct = async (productId, productData) => {
  const resJson = await put(`/products/${productId}`, productData)
  return resJson.product;
}

export const deleteProduct = async (productId) => {
  const resJson = await del(`/products/${productId}`)
  return resJson;
}

export const reactivateProduct = async (productId) => {
  const resJson = await post(`/products/${productId}/reactivate`)
  return resJson.product;
}

export const fetchReceivables = async (options = {}) => {
  const params = new URLSearchParams()

  // Add all provided parameters to the query string
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })

  const queryString = params.toString()
  const url = `/receivables${queryString ? '?' + queryString : ''}`

  const resJson = await get(url)
  return resJson;
}

export const fetchPayments = async (options = {}) => {
  const params = new URLSearchParams()

  // Add all provided parameters to the query string
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })

  const queryString = params.toString()
  const url = `/payments${queryString ? '?' + queryString : ''}`

  const resJson = await get(url)
  return resJson;
}

export const fetchPaymentById = async (paymentId) => {
  const resJson = await get(`/payments/${paymentId}`)
  return resJson.payment;
}

export const createPayment = async (paymentData) => {
  const resJson = await post('/payments', paymentData)
  return resJson.payment;
}

export const refundPayment = async (paymentId, refundData) => {
  const resJson = await post(`/payments/${paymentId}/refund`, refundData)
  return resJson.payment;
}

// Patient Notes API

export const fetchPatientNotes = async (options = {}) => {
  const params = new URLSearchParams()
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })
  const queryString = params.toString()
  const url = `/patient-notes${queryString ? '?' + queryString : ''}`
  const resJson = await get(url)
  return resJson;
}

export const fetchPatientNoteById = async (patientNoteId) => {
  const resJson = await get(`/patient-notes/${patientNoteId}`)
  return resJson.patientNote;
}

export const createPatientNote = async (noteData) => {
  const resJson = await post('/patient-notes', noteData)
  return resJson.patientNote;
}

export const updatePatientNote = async (patientNoteId, noteData) => {
  const resJson = await put(`/patient-notes/${patientNoteId}`, noteData)
  return resJson.patientNote;
}

export const deletePatientNote = async (patientNoteId) => {
  const resJson = await del(`/patient-notes/${patientNoteId}`)
  return resJson;
}

// Distribution Events API

export const fetchDistributionEvents = async (options = {}) => {
  const params = new URLSearchParams()
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value.toString())
    }
  })
  const queryString = params.toString()
  const url = `/distribution-events${queryString ? '?' + queryString : ''}`
  const resJson = await get(url)
  return resJson;
}

export const createDistributionEvent = async (eventData) => {
  const resJson = await post('/distribution-events', eventData)
  return resJson.distributionEvent;
}

export const updateDistributionEvent = async (distributionEventId, eventData) => {
  const resJson = await put(`/distribution-events/${distributionEventId}`, eventData)
  return resJson.distributionEvent;
}

export const deleteDistributionEvent = async (distributionEventId) => {
  const resJson = await del(`/distribution-events/${distributionEventId}`)
  return resJson;
}

// TRT Program API

export const enrollTrtPatient = async (patientId) => {
  const resJson = await post('/trt', { patientId })
  return resJson;
}

export const removeTrtPatient = async (patientId) => {
  const resJson = await del(`/trt/${patientId}`)
  return resJson;
}

export const fetchTrtCards = async () => {
  const resJson = await get('/trt/cards')
  return resJson.cards;
}

export const syncTrtData = async (patientId, trtData) => {
  const resJson = await put(`/trt/${patientId}/data`, { trtData })
  return resJson;
}

export const setTrtMonitorDate = async (patientId, monitorDate) => {
  const resJson = await put(`/trt/${patientId}/monitor-date`, { monitorDate })
  return resJson;
}

// Analytics API
export const fetchDailyCounts = async ({ date } = {}) => {
  const params = new URLSearchParams()
  params.append('date', date)

  const queryString = params.toString()
  const url = `/analytics/daily-counts?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchDailyTickets = async ({ date } = {}) => {
  const params = new URLSearchParams()
  params.append('date', date)

  const queryString = params.toString()
  const url = `/analytics/daily-tickets?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchSalesTrend = async ({ startDate, endDate, intervalType } = {}) => {
  const params = new URLSearchParams()
  params.append('startDate', startDate)
  params.append('endDate', endDate)
  params.append('intervalType', intervalType)

  const queryString = params.toString()
  const url = `/analytics/sales-trend?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchDailySalesReport = async ({ date } = {}) => {
  const params = new URLSearchParams()
  params.append('date', date)

  const queryString = params.toString()
  const url = `/analytics/daily-sales-report?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchDailySalesTickets = async ({ date } = {}) => {
  const params = new URLSearchParams()
  params.append('date', date)

  const queryString = params.toString()
  const url = `/analytics/daily-sales-tickets?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchCounselorsComparison = async ({ startDate, endDate } = {}) => {
  const params = new URLSearchParams()
  params.append('startDate', startDate)
  params.append('endDate', endDate)

  const queryString = params.toString()
  const url = `/analytics/counselors-comparison?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchCounselorsSalesTrend = async ({ startDate, endDate, counselorIds, intervalType } = {}) => {
  const params = new URLSearchParams()
  params.append('startDate', startDate)
  params.append('endDate', endDate)
  params.append('intervalType', intervalType)
  if (counselorIds && counselorIds.length > 0) {
    params.append('counselorIds', counselorIds.join(','))
  }

  const queryString = params.toString()
  const url = `/analytics/counselors-sales-trend?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchProductsComparison = async ({ startDate, endDate } = {}) => {
  const params = new URLSearchParams()
  params.append('startDate', startDate)
  params.append('endDate', endDate)

  const queryString = params.toString()
  const url = `/analytics/products-comparison?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchProductsSalesTrend = async ({ startDate, endDate, productIds, intervalType } = {}) => {
  const params = new URLSearchParams()
  params.append('startDate', startDate)
  params.append('endDate', endDate)
  params.append('intervalType', intervalType)
  if (productIds && productIds.length > 0) {
    params.append('productIds', productIds.join(','))
  }

  const queryString = params.toString()
  const url = `/analytics/products-sales-trend?${queryString}`

  const resJson = await get(url)
  return resJson
}

export const fetchFinancialsTrend = async ({ startDate, endDate, intervalType } = {}) => {
  const params = new URLSearchParams()
  params.append('startDate', startDate)
  params.append('endDate', endDate)
  params.append('intervalType', intervalType)

  const url = `/analytics/financials-trend?${params.toString()}`

  const resJson = await get(url)
  return resJson
}

export const fetchCounselorDetails = async ({ startDate, endDate, intervalType, counselorId } = {}) => {
  const params = new URLSearchParams()
  params.append('startDate', startDate)
  params.append('endDate', endDate)
  params.append('intervalType', intervalType)
  params.append('counselorId', counselorId)

  const url = `/analytics/counselor-details?${params.toString()}`

  const resJson = await get(url)
  return resJson
}

export const fetchProductDetails = async ({ startDate, endDate, intervalType, productId } = {}) => {
  const params = new URLSearchParams()
  params.append('startDate', startDate)
  params.append('endDate', endDate)
  params.append('intervalType', intervalType)
  params.append('productId', productId)

  const url = `/analytics/product-details?${params.toString()}`

  const resJson = await get(url)
  return resJson
}

// HELPERS

const post = (url, body, signal) => request('POST', url, body, signal);

const get = (url, signal) => request('GET', url, null, signal);

const put = (url, body, signal) => request('PUT', url, body, signal);

const del = (url, signal) => request('DELETE', url, null, signal);

async function request(method, url, body = null, signal = null) {
  const headers = { 'Content-Type': 'application/json' }

  const practiceId = getPracticeId()
  if (practiceId) {
    headers['x-practice-id'] = practiceId
  }

  const options = {
    method: method,
    headers: headers,
    signal: signal,
    body: body ? JSON.stringify(body) : undefined
  };

  if (extensionAuth.isExtensionMode()) {
    // Extension mode: never send cookies; authenticate via the bearer header.
    // The extension owns the token, so fetch it over the message protocol for
    // each request rather than trusting a (possibly empty) in-memory cache.
    options.credentials = 'omit'
    headers['X-Extension-Mode'] = 'true'
    const token = await extensionAuth.loadToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  } else {
    options.credentials = 'include'
  }

  const res = await fetch(process.env.VUE_APP_API + url, options);
  const json = await res.json().catch(() => ({}));

  if (res.status === 401) {
    import('@/router').then(({ default: router }) => {
      if (router.currentRoute.value.path !== '/login') {
        router.push('/login')
      }
    })
    throw Error(json.error || 'Permission denied');
  }

  if (res.status !== 200 && res.status !== 201) throw Error(json.error || res.statusText);

  return json;
}